import countries from 'i18n-iso-countries'
import esLocale from 'i18n-iso-countries/langs/es.json'
import enLocale from 'i18n-iso-countries/langs/en.json'
import ruLocale from 'i18n-iso-countries/langs/ru.json'

// Register locales
countries.registerLocale(esLocale)
countries.registerLocale(enLocale)
countries.registerLocale(ruLocale)

export type SupportedLanguage = 'es' | 'en' | 'ru'

export interface CountryOption {
  alpha2: string
  alpha3: string
  name: string
}

/**
 * Get country name from ISO 3166-1 alpha-3 code
 */
export function getCountryName(alpha3: string, lang: SupportedLanguage): string {
  if (!alpha3) return ''
  const alpha2 = countries.alpha3ToAlpha2(alpha3.toUpperCase())
  if (!alpha2) return alpha3
  return countries.getName(alpha2, lang) || alpha3
}

/**
 * Get all countries sorted by name for a given language
 */
export function getAllCountries(lang: SupportedLanguage): CountryOption[] {
  const countryObj = countries.getNames(lang)
  return Object.entries(countryObj)
    .map(([alpha2, name]) => ({
      alpha2,
      alpha3: countries.alpha2ToAlpha3(alpha2) || alpha2,
      name: name as string,
    }))
    .filter(c => c.alpha3 && c.alpha3.length === 3) // Filter out invalid
    .sort((a, b) => a.name.localeCompare(b.name, lang))
}

/**
 * Convert ISO 3166-1 alpha-2 to alpha-3
 */
export function alpha2ToAlpha3(alpha2: string): string {
  if (!alpha2) return ''
  return countries.alpha2ToAlpha3(alpha2.toUpperCase()) || alpha2
}

/**
 * Convert ISO 3166-1 alpha-3 to alpha-2
 */
export function alpha3ToAlpha2(alpha3: string): string {
  if (!alpha3) return ''
  return countries.alpha3ToAlpha2(alpha3.toUpperCase()) || alpha3
}

/**
 * Validate if a string is a valid ISO 3166-1 alpha-3 code
 */
export function isValidAlpha3(code: string): boolean {
  if (!code || code.length !== 3) return false
  const alpha2 = countries.alpha3ToAlpha2(code.toUpperCase())
  return !!alpha2
}

/**
 * Common countries to show at the top of the list (for quick access)
 * These are the most commonly used countries in the application
 */
export const COMMON_COUNTRY_CODES = ['ESP', 'USA', 'GBR', 'DEU', 'FRA', 'ITA', 'PRT', 'RUS']

/**
 * Get countries with common ones at the top
 */
export function getCountriesWithCommonFirst(lang: SupportedLanguage): CountryOption[] {
  const allCountries = getAllCountries(lang)
  const commonCountries = COMMON_COUNTRY_CODES
    .map(code => allCountries.find(c => c.alpha3 === code))
    .filter((c): c is CountryOption => c !== undefined)

  const otherCountries = allCountries.filter(
    c => !COMMON_COUNTRY_CODES.includes(c.alpha3)
  )

  return [...commonCountries, ...otherCountries]
}
